<template>
  <v-card :disabled="disabled" color="surface lighten-1" elevation="3" :class="cardClass">
    <v-btn class="open-settings" icon @click="openSettings">
      <v-icon>mdi-pencil</v-icon>
    </v-btn>
    <v-btn v-if="isSupervisorCard" class="open-settings second-option" x-small icon @click="displayShowColumns">
      <v-icon>
        mdi-cogs
      </v-icon>
    </v-btn>
    <v-carousel
      v-model="currentSlide"
      class="wallboard-card"
      :class="'wallboard-card-font-' + fontSize"
      :height="cardSize > 0 ? 350 * cardSize : ''"
      :continuous="true"
      :show-arrows="false"
      hide-delimiter-background
      :hide-delimiters="hideDelimiter"
      vertical-delimiters
      vertical
      :cycle="cycle"
      :interval="getSlidesInterval * 1000"
    >
      <div class="card-title">{{ fallbackCard.name }}</div>
      <v-carousel-item v-for="slide in fallbackCard.slides" :key="slide.id">
        <div class="title-container">
          <div v-if="!isSupervisorCard" :class="'slide-title ' + (slide.name.length > 50 ? 'scroll-text' : '')">
            {{ slide.name }}
          </div>

          <v-spacer />
        </div>

        <div class="total-stat-container">
          <component :is="fallbackCard.componentName" ref="cardComponent" :slide="slide" :card="card" />
        </div>
      </v-carousel-item>
    </v-carousel>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex';
import EcsDashboardCard from '@/components/ecs-dashboard-card/EcsDashboardCard';
import EcsAgentsStatesSlide from '@/components/ecs-wallboard-card/ecs-agents-states-slide/EcsAgentsStatesSlide';
import EcsAverageRingTimeSlide from '@/components/ecs-wallboard-card/ecs-average-ring-time-slide/EcsAverageRingTimeSlide';
import EcsAverageSpeedAnswerSlide from '@/components/ecs-wallboard-card/ecs-average-speed-answer-slide/EcsAverageSpeedAnswerSlide';
import EcsInteractionsSlide from '@/components/ecs-wallboard-card/ecs-interactions-slide/EcsInteractionsSlide';
import EcsLongestWaitingTimeSlide from '@/components/ecs-wallboard-card/ecs-longest-waiting-time-slide/EcsLongestWaitingTimeSlide';
import EcsServiceLevelSlide from '@/components/ecs-wallboard-card/ecs-service-level-slide/EcsServiceLevelSlide';
import EcsCallbacksSlide from '@/components/ecs-wallboard-card/ecs-callbacks-slide/EcsCallbacksSlide';
import EcsSupervisedAgentsSlide from '@/components/ecs-wallboard-card/ecs-supervised-agents-slide/EcsSupervisedAgentsSlide';
import EcsSupervisedInteractionsSlide from '@/components/ecs-wallboard-card/ecs-supervised-interactions-slide/EcsSupervisedInteractionsSlide';

import EcsKpiFormatter from '@/mixins/ecs-kpi-formatter/EcsKpiFormatter';

export default {
  name: 'EcsWallboardCard',

  components: {
    EcsDashboardCard,
    EcsAgentsStatesSlide,
    EcsAverageRingTimeSlide,
    EcsAverageSpeedAnswerSlide,
    EcsInteractionsSlide,
    EcsLongestWaitingTimeSlide,
    EcsServiceLevelSlide,
    EcsCallbacksSlide,
    EcsSupervisedAgentsSlide,
    EcsSupervisedInteractionsSlide
  },

  mixins: [EcsKpiFormatter],

  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    expansible: {
      type: Boolean,
      default: true
    },
    card: {
      type: Object,
      default() {
        return {};
      }
    },
    freezeWallboard: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      currentSlide: 0,
      fontSize: 1,
      expanded: false,
      supervisorCards: ['EcsSupervisedAgentsSlide', 'EcsSupervisedInteractionsSlide']
    };
  },
  computed: {
    ...mapGetters('WALLBOARD', ['getSlidesInterval', 'getWallboardCards', 'getWallboardFontSize']),

    ...mapGetters('AGENT', ['isSupervisor']),

    /**
     * Update old card component names to prevent misrender
     */
    fallbackCard() {
      const fallbackCard = { ...this.card, componentName: this.card.componentName.replace('Card', 'Slide') };
      return fallbackCard;
    },

    cycle() {
      return this.fallbackCard.slides.length > 1 && !this.freezeWallboard;
    },

    cardClass() {
      if (!this.isSupervisor) {
        if (this.fallbackCard.sizeRatio) {
          const sizeNumber = { small: 1, medium: 2, large: 3 };
          return 'wallboard-card wallboard-card--' + sizeNumber[this.fallbackCard.sizeRatio.toLowerCase()];
        }

        return 'wallboard-card';
      } else {
        return this.isSupervisorCard ? 'supervisor-card' : 'wallboard-card';
      }
    },

    cardSize() {
      if (this.isSupervisor) {
        return 0;
      }
      const sizeNumber = { small: 1, medium: 2, large: 3 };
      return sizeNumber[this.fallbackCard.sizeRatio.toLowerCase()];
    },

    hideDelimiter() {
      return this.fallbackCard.slides.length === 1;
    },

    isSupervisorCard() {
      return this.supervisorCards.includes(this.fallbackCard.componentName);
    }
  },

  watch: {
    getWallboardFontSize() {
      this.fontSize = this.getWallboardFontSize;
    }
  },

  mounted() {
    this.fontSize = this.getWallboardFontSize;
  },

  methods: {
    openSettings() {
      this.$emit('openModal', this.fallbackCard);
    },

    displayShowColumns() {
      this.$refs.cardComponent[this.currentSlide].showColumns();
    }
  }
};
</script>

<style src="./scoped.css" scoped></style>

<style>
.wallboard-card .v-btn::before {
  background: black !important;
}
.wallboard-card .v-btn--active::before {
  opacity: 0.3;
}

.wallboard-card .v-image {
  height: 100%;
}
</style>
